import React, { useState } from 'react';
import { Heart, ChevronDown, ChevronUp } from 'lucide-react';
import { Market, MarketSearch } from '../../types/market';
import { useChatInput } from '../../contexts/ChatContext';
import { useUserLikes } from '../../hooks/useUserLikes';
import { useAuth } from '../auth/AuthContext';
import { SignInPrompt } from '../auth/SignInPrompt';
import { SearchPagination } from '../ui/SearchPagination';
import { useNavigate } from 'react-router-dom';

const ITEMS_PER_PAGE = 5;

interface MarketSearchesProps {
  market: Market;
  searches: MarketSearch[];
}

export const MarketSearches: React.FC<MarketSearchesProps> = ({ market, searches }) => {
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const { isLiked, toggleLike } = useUserLikes();
  const { user } = useAuth();
  const { setInputValue } = useChatInput();
  const navigate = useNavigate();

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentSearches = searches.slice(startIndex, endIndex);

  const handleLikeClick = async (e: React.MouseEvent, searchId: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setShowSignInPrompt(true);
      return;
    }
    await toggleLike(searchId);
  };

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSearchClick = (query: string, searchId: string) => {
    setInputValue(query);
    navigate(`/markets/${market.id}`, { 
      state: { 
        query,
        searchMetadata: {
          searchId,
          searchCategory: 'market_search'
        }
      } 
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedItems(new Set());
  };

  const isTextTruncated = (text: string): boolean => {
    const words = text.split(' ');
    const estimatedChars = text.length;
    return estimatedChars > 50 || words.length > 12;
  };

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{market.name}</h2>
          {market.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{market.description}</p>
          )}
        </div>
        
        <div className="p-6">
          {searches.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400">
              No searches available for this market
            </p>
          ) : (
            <>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {currentSearches.map((item) => (
                  <div
                    key={item.id}
                    className="block py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer"
                    onClick={() => handleSearchClick(item.search.query, item.search.id)}
                  >
                    <div>
                      <p className={`text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white ${
                        expandedItems.has(item.id) ? '' : 'line-clamp-2'
                      }`}>
                        {item.search.query}
                      </p>
                      {isTextTruncated(item.search.query) && (
                        <div
                          onClick={(e) => toggleExpand(e, item.id)}
                          className="mt-1 text-blue-600 dark:text-blue-400 text-sm flex items-center gap-1 cursor-pointer hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {expandedItems.has(item.id) ? (
                            <>
                              Show less
                              <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Show more
                              <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                      <span>{item.search.searches.toLocaleString()} searches</span>
                      <span className="text-gray-300 dark:text-gray-600">•</span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {item.search.likes}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <SearchPagination
                currentPage={currentPage}
                totalItems={searches.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>

      {showSignInPrompt && (
        <SignInPrompt onClose={() => setShowSignInPrompt(false)} />
      )}
    </>
  );
};